'use client';

import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { ProjectAccess, ProjectPermission } from '../lib/api/projectPermissions';
import {
  isProjectAccessDeniedError,
  isProjectAccessQueryEnabled,
  projectAccessQueryOptions,
} from './queries/projectAccessQuery';
import { useWorkspaceScope } from './useWorkspaceScope';

export type UseProjectPermissionsResult = {
  permissions: ProjectPermission[];
  roles: ProjectAccess['roles'];
  /** permission을 아직 모르는 상태. 이 동안은 권한이 필요한 UI를 그리지 않는다. */
  isLoading: boolean;
  /**
   * 판정 대상 프로젝트가 정해졌는지.
   *
   * `false`는 "권한 없음"이 아니라 "아직 판정할 대상이 없음"이다. 호출부가 이 둘을 섞으면
   * 프로젝트가 정해지기 전에 접근 거부 UI가 먼저 그려진다.
   */
  isProjectResolved: boolean;
  /** 오프라인 등으로 요청이 멈춘 상태(react-query `paused`). 로딩과 구분해 안내해야 한다. */
  isPaused: boolean;
  error: unknown;
  can: (permission: ProjectPermission) => boolean;
  canAny: (...permissions: ProjectPermission[]) => boolean;
  refetch: () => void;
};

const EMPTY_PERMISSIONS: ProjectPermission[] = [];

/**
 * 현재 사용자의 프로젝트 유효 permission.
 *
 * 서버가 최종 보안 경계이고 이 훅은 **화면 안내용**이다. 다만 로딩 중에 권한 있는 화면을
 * 먼저 그렸다가 지우면 제한된 메뉴가 깜빡여 노출되므로, 판정 전에는 항상 "권한 없음"으로 다룬다.
 *
 * 권한을 알아내려고 멤버 전체 목록을 받아오지 않는다 — 전용 access 엔드포인트만 쓴다.
 */
export const useProjectPermissions = (overrides?: { projectId?: string }): UseProjectPermissionsResult => {
  const scope = useWorkspaceScope();
  const token = scope.token;
  const projectId = overrides?.projectId ?? scope.projectId;

  // 토큰이나 프로젝트가 없으면 판정 자체가 성립하지 않는다. 그건 "로딩"이 아니라 "판정 대상 아님"이다.
  const isEnabled = isProjectAccessQueryEnabled(token, projectId);

  // 쿼리 옵션(retry/staleTime)은 `projectAccessQueryOptions`가 SSOT다. 이동 직전 선조회(fetchQuery)와
  // 계약이 갈리면 같은 엔드포인트가 호출부마다 다르게 동작한다.
  const accessQuery = useQuery(projectAccessQueryOptions(token, projectId));

  // 셸과 게이트 모두 마지막 권한을 유지한다. 백그라운드 재검증 중에도 캐시된 판정을
  // 그대로 쓰고, 서버가 접근 상실(403·404)을 확정했을 때만 비운다. 타임아웃·5xx까지 권한 없음으로
  // 보면 재검증 실패 한 번에 보고 있던 화면이 접근 불가 안내로 밀려난다 — 이 쿼리는 재시도가 없다.
  // 아직 캐시가 없는 최초 판정은 어떤 실패든 권한 없음이다(보여줄 이전 판정이 없다).
  const isAccessDenied = accessQuery.isError && isProjectAccessDeniedError(accessQuery.error);
  const permissions = isAccessDenied ? EMPTY_PERMISSIONS : (accessQuery.data?.permissions ?? EMPTY_PERMISSIONS);
  const permissionSet = useMemo(() => new Set(permissions), [permissions]);

  const can = useCallback((permission: ProjectPermission) => permissionSet.has(permission), [permissionSet]);
  const canAny = useCallback(
    (...candidates: ProjectPermission[]) => candidates.some((permission) => permissionSet.has(permission)),
    [permissionSet],
  );

  const queryRefetch = accessQuery.refetch;
  const refetchAccess = useCallback(() => {
    void queryRefetch();
  }, [queryRefetch]);

  return {
    permissions,
    // 권한과 같은 판정에서 나와야 한다. 접근 상실이 확정됐는데 역할만 남으면 화면이 서로 다른 말을 한다.
    roles: isAccessDenied ? [] : (accessQuery.data?.roles ?? []),
    // 쿼리가 켜져 있는데 아직 끝나지 않았으면 로딩이다. `fetchStatus`를 함께 보면 안 된다 —
    // 영속 캐시(PersistQueryClientProvider) 복원 중에는 query-core가 `fetchStatus`를 'idle'로
    // 눌러버려서 "아직 모름"이 "판정 끝, 권한 없음"으로 둔갑하고 접근 거부 화면이 선렌더된다.
    isLoading: isEnabled && accessQuery.isPending,
    isProjectResolved: projectId.length > 0,
    // 오프라인이면 쿼리가 `paused`로 멈춘 채 `isPending`을 유지한다. 이걸 로딩으로만 흘리면
    // 원인 안내도 재시도 수단도 없이 로더만 계속 돈다.
    isPaused: isEnabled && accessQuery.isPending && accessQuery.fetchStatus === 'paused',
    error: accessQuery.error,
    can,
    canAny,
    refetch: refetchAccess,
  };
};
